/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */
import { createSlice } from '@reduxjs/toolkit';

import { removePreviousPrimaryContact } from 'common/utils/document-utils';
import { initialWorkOrder } from './constants';

const initialState = {
    initialized: false,
    assetsInitialized: false,
    filesInitialized: false,
    historyInitialized: false,
    detailsInitialized: false,
    lineItemsInitialized: false,
    requirementsInitialized: false,
    workOrder: initialWorkOrder,
    snapshot: null,
    clientCustomFields: [],
    projects: null,
    ncfContractors: null,
    finances: null,
    isClientEnablePhotoVerification: false,
    accountIdEnforced: false,
    dynamicDispatchingCredentials: null,
    bundlePricing: null,
};

export const createWorkOrderSlice = sliceName => {
    const workOrderSlice = createSlice({
        name: sliceName,
        initialState,
        reducers: {
            /**
             * Initializes the work order store with work order, projects, snapshot, and other data
             */
            init: (state, action) => {
                const {
                    workOrder,
                    projects,
                    snapshot,
                    ncfContractors,
                    clientCustomFields,
                    accountIdEnforced,
                } = action.payload;
                return {
                    ...state,
                    initialized: true,
                    workOrder,
                    projects,
                    snapshot,
                    ncfContractors,
                    clientCustomFields,
                    accountIdEnforced,
                };
            },

            /**
             * Replaces the current work order with a new work order
             */
            replace: (state, action) => {
                return {
                    ...state,
                    workOrder: action.payload,
                };
            },

            /**
             * Updates work order with provided updates object
             */
            update: (state, action) => {
                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        ...action.payload,
                    },
                };
            },

            /**
             * Resets all page initialization flags
             */
            resetPages: state => {
                return {
                    ...state,
                    initialized: false,
                    detailsInitialized: false,
                    lineItemsInitialized: false,
                    assetsInitialized: false,
                    requirementsInitialized: false,
                    filesInitialized: false,
                    historyInitialized: false,
                };
            },

            /**
             * Initializes a specific route by setting its initialization flag
             * Payload: string (route name)
             */
            initializeRoute: (state, action) => {
                return {
                    ...state,
                    [`${action.payload}Initialized`]: true,
                };
            },

            /**
             * Replaces the snapshot
             */
            replaceSnapshot: (state, action) => {
                return {
                    ...state,
                    snapshot: action.payload,
                };
            },

            /**
             * Updates work order property or properties
             * Payload: { key: string | string[], value: any | any[] }
             */
            updateKeyValuePair: (state, action) => {
                const { key, value } = action.payload;
                const update = {};

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        update[k] = value[idx];
                    });
                } else {
                    update[key] = value;
                }

                return {
                    ...state,
                    workOrder: { ...state.workOrder, ...update },
                };
            },

            /**
             * Updates work order instructions
             * Payload: { key: string | string[], value: any | any[] }
             */
            updateInstructions: (state, action) => {
                const { key, value } = action.payload;
                const update = {};

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        update[k] = value[idx];
                    });
                } else {
                    update[key] = value;
                }

                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        instructions: { ...update },
                    },
                };
            },

            /**
             * Updates a contact by id
             * Payload: { key: string | string[], value: any | any[], id: string }
             */
            updateContact: (state, action) => {
                const { key, value, id } = action.payload;
                const update = {};
                let contacts = state.workOrder.contacts.filter(
                    c => c.id !== id
                );

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        if (k === 'primaryContact' && value[idx] === true) {
                            contacts = removePreviousPrimaryContact(
                                id,
                                state.workOrder.contacts,
                                contacts
                            );
                        }
                        update[k] = value[idx];
                    });
                } else {
                    if (key === 'primaryContact' && value === true) {
                        contacts = removePreviousPrimaryContact(
                            id,
                            state.workOrder.contacts,
                            contacts
                        );
                    }
                    update[key] = value;
                }

                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        contacts: [...contacts, update],
                    },
                };
            },

            /**
             * Deletes a contact by id
             * Payload: string (id)
             */
            deleteContact: (state, action) => {
                const contacts = state.workOrder.contacts.filter(
                    c => c.id !== action.payload
                );
                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        contacts,
                    },
                };
            },

            /**
             * Adds a new contact
             * Payload: object (contact)
             */
            addContact: (state, action) => {
                const contacts = [
                    ...(state.workOrder.contacts ?? []),
                    action.payload,
                ];

                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        contacts,
                    },
                };
            },

            /**
             * Adds a note to the job
             * Payload: object (note)
             */
            addNote: (state, action) => {
                const currentNotes = state.workOrder.job.notes || [];
                const notes = [...currentNotes, action.payload];

                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        job: { ...state.workOrder.job, notes },
                    },
                };
            },

            /**
             * Updates a note by id
             * Payload: object (note with id)
             */
            updateNote: (state, action) => {
                const notes = [...state.workOrder.job.notes];
                const idx = notes.findIndex(n => n.id === action.payload.id);

                if (idx !== -1) {
                    notes[idx] = action.payload;
                }

                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        job: { ...state.workOrder.job, notes },
                    },
                };
            },

            /**
             * Adds a comment to the work order
             * Payload: object (comment)
             */
            addComment: (state, action) => {
                const currentComments = state.workOrder.comments || [];
                const comments = [...currentComments, action.payload];

                return {
                    ...state,
                    workOrder: { ...state.workOrder, comments },
                };
            },

            /**
             * Updates a comment by id
             * Payload: object (comment with id)
             */
            updateComment: (state, action) => {
                const comments = [...state.workOrder.comments];
                const idx = comments.findIndex(c => c.id === action.payload.id);

                if (idx !== -1) {
                    comments[idx] = action.payload;
                }

                return {
                    ...state,
                    workOrder: { ...state.workOrder, comments },
                };
            },

            /**
             * Initializes financial information
             */
            initFinancialInformation: (state, action) => {
                return {
                    ...state,
                    finances: action.payload,
                };
            },

            /**
             * Updates payment information with work order and snapshot
             */
            updatePayment: (state, action) => {
                const { workOrder, snapshot } = action.payload;
                return {
                    ...state,
                    initialized: true,
                    workOrder,
                    snapshot,
                };
            },

            /**
             * Updates billing information with work order and snapshot
             */
            updateBilling: (state, action) => {
                const { workOrder, snapshot } = action.payload;
                return {
                    ...state,
                    initialized: true,
                    workOrder,
                    snapshot,
                };
            },

            /**
             * Adds a photo to the work order
             * Payload: object (photo)
             */
            addPhoto: (state, action) => {
                const photos = state.workOrder.photos || [];
                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        photos: [...photos, action.payload],
                    },
                };
            },

            /**
             * Removes a photo by id
             * Payload: string (id)
             */
            removePhoto: (state, action) => {
                const photos = [...state.workOrder.photos];
                const idx = photos.findIndex(p => p.id === action.payload);
                if (idx !== -1) {
                    photos.splice(idx, 1);
                    return {
                        ...state,
                        workOrder: { ...state.workOrder, photos },
                    };
                }
                return state;
            },

            /**
             * Adds a folder attachment to the work order
             * Payload: object (folderAttachment)
             */
            addFolderAttachment: (state, action) => {
                const folderAttachments = [
                    ...(state.workOrder.folder.folderAttachments || []),
                    action.payload,
                ];

                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        folder: {
                            ...state.workOrder.folder,
                            folderAttachments,
                        },
                    },
                };
            },

            /**
             * Removes a folder attachment by id
             * Payload: string (id)
             */
            removeFolderAttachment: (state, action) => {
                const folderAttachments = [
                    ...state.workOrder.folder.folderAttachments,
                ];
                const idx = folderAttachments.findIndex(
                    fa => fa.id === action.payload
                );
                if (idx !== -1) {
                    folderAttachments.splice(idx, 1);
                    return {
                        ...state,
                        workOrder: {
                            ...state.workOrder,
                            folder: {
                                ...state.workOrder.folder,
                                folderAttachments,
                            },
                        },
                    };
                }
                return state;
            },

            /**
             * Sets finance edit mode and manages original line items
             * Payload: boolean (financeEdit)
             */
            setFinanceEdit: (state, action) => {
                const financeEdit = action.payload;
                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        financeEdit,
                        originalLineItems: financeEdit
                            ? state.workOrder.lineItems
                            : undefined,
                        lineItems: financeEdit
                            ? state.workOrder.lineItems
                            : state.workOrder.originalLineItems,
                    },
                };
            },
            setClientPhotoVerification: (state, action) => {
                return {
                    ...state,
                    isClientEnablePhotoVerification: action.payload,
                };
            },
            setContractorTeamMembers: (state, action) => {
                const _teamMembers = action.payload;
                return {
                    ...state,
                    workOrder: {
                        ...state.workOrder,
                        teamMembers: _teamMembers,
                    },
                };
            },
            setDynamicDispatchingCredentials: (state, action) => {
                return {
                    ...state,
                    dynamicDispatchingCredentials: action.payload,
                };
            },
            setBundlePricing: (state, action) => {
                return {
                    ...state,
                    bundlePricing: action.payload,
                };
            },
        },
    });

    return {
        reducer: workOrderSlice.reducer,
        actions: workOrderSlice.actions,
    };
};
